import { useRouter } from 'next/router'
import { VFC, useEffect, useState } from 'react'
import { useContents } from 'src/api/content'
import {
  CONTENT_TAG_BANNER_1,
  CONTENT_TAG_BANNER_2,
  CONTENT_TAG_BANNER_3,
  CONTENT_TAG_BANNER_4,
  CONTENT_TAG_BANNER_5,
  CONTENT_TAG_BANNER_6,
  CONTENT_TAG_BANNER_7,
  CONTENT_TAG_BANNER_8,
} from 'src/assets/images'
import { ContentsMoreIcon, CrownIcon, SearchIcon } from 'src/assets/svgs'
import { Link } from 'src/elements/Link'
import { fontWeightBold } from 'src/styles/font'
import { contentDetail, searchContents } from 'src/utils/routes'
import styled from 'styled-components'
import { ContentCard } from './common'

export const ContentPageLayout: VFC = () => {
  const router = useRouter()
  const { data: contents } = useContents()

  const newContents = contents?.newContents ?? []
  const bookmarkedContents = contents?.bookmarkedContents ?? []
  const popularContents = contents?.popularContents ?? []
  const weeklyContents = contents?.weeklyContents ?? []

  // 現在のアクティブなセクションを管理するstate
  const [activeSection, setActiveSection] = useState<string>('new-contents')
  // スクロールアニメーション中かどうかを管理するstate
  const [isScrolling, setIsScrolling] = useState<boolean>(false)
  // 検索キーワードのstate
  const [searchKeyword, setSearchKeyword] = useState<string>('')

  const tagCategories = [
    {
      title: '経営戦略・事業開発',
      image: CONTENT_TAG_BANNER_1,
    },
    {
      title: 'ブランディング・マーケティング',
      image: CONTENT_TAG_BANNER_2,
    },
    {
      title: 'セールス強化・売上拡大',
      image: CONTENT_TAG_BANNER_3,
    },
    {
      title: '人材採用',
      image: CONTENT_TAG_BANNER_4,
    },
    {
      title: '人・組織づくり',
      image: CONTENT_TAG_BANNER_5,
    },
    {
      title: '業務改善・システム開発・デジタル化',
      image: CONTENT_TAG_BANNER_6,
    },
    {
      title: '財務・資金管理・バックオフィス',
      image: CONTENT_TAG_BANNER_7,
    },
    {
      title: 'その他',
      image: CONTENT_TAG_BANNER_8,
    },
  ]

  // 「もっと見る」用 state
  const [activeRankingTab, setActiveRankingTab] = useState<
    'popular' | 'weekly'
  >('popular')

  const DISPLAY_LIMIT = 6
  const displayedNewContents = newContents.slice(0, DISPLAY_LIMIT)
  const displayedBookmarkedContents = bookmarkedContents.slice(0, DISPLAY_LIMIT)

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (element) {
      // スクロール開始時にフラグを設定
      setIsScrolling(true)
      setActiveSection(sectionId)

      const headerHeight = 90 // ヘッダーの高さを考慮
      const elementPosition = element.offsetTop - headerHeight
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth',
      })

      // スクロールアニメーション完了後にフラグをリセット
      setTimeout(() => {
        setIsScrolling(false)
      }, 2000) // スクロールアニメーションの時間に応じて調整
    }
  }

  // キーワード検索を実行する関数
  const handleSearch = () => {
    if (searchKeyword.trim())
      router.push(
        `/contents/search?orderBy=recent&page=1&keyword=${encodeURIComponent(
          searchKeyword.trim(),
        )}`,
      )
  }

  // Enterキーで検索を実行する関数
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch()
  }

  // スクロール位置に基づいてアクティブなセクションを自動更新
  useEffect(() => {
    const handleScroll = () => {
      // スクロールアニメーション中は処理をスキップ
      if (isScrolling) return

      const sections = ['new-contents', 'tag-search', 'bookmark-contents']
      const headerHeight = 90
      const scrollPosition = window.scrollY + headerHeight + 100 // オフセットを追加

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i])
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i])
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isScrolling]) // isScrollingを依存配列に追加

  return (
    <>
      <Layout>
        {/* Search Header */}
        <SearchHeader>
          <SearchContainer>
            <SearchTitle>経営コラム</SearchTitle>
            <SearchWrapper>
              <SearchInput
                type="text"
                placeholder="キーワードを入れて検索"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <SearchIconWrapper onClick={handleSearch}>
                <SearchIcon />
              </SearchIconWrapper>
            </SearchWrapper>
          </SearchContainer>
        </SearchHeader>

        {/* Main Layout */}
        <MainLayout>
          {/* Sidebar */}
          <Sidebar>
            <SidebarNav>
              <NavItem
                className={activeSection === 'new-contents' ? 'active' : ''}
                onClick={() => scrollToSection('new-contents')}
              >
                <NavText>新着</NavText>
              </NavItem>
              <NavItem
                className={activeSection === 'tag-search' ? 'active' : ''}
                onClick={() => scrollToSection('tag-search')}
              >
                <NavText>タグ検索</NavText>
              </NavItem>
              <NavItem
                className={
                  activeSection === 'bookmark-contents' ? 'active' : ''
                }
                onClick={() => scrollToSection('bookmark-contents')}
              >
                <NavText>お気に入り</NavText>
              </NavItem>
            </SidebarNav>
          </Sidebar>

          {/* Main Content Area */}
          <ContentArea>
            {/* Left Column */}
            <MainContent>
              {/* New Contents Section */}
              <Section id="new-contents">
                <SectionHeader>
                  <SectionTitleWrapper>
                    <SectionSubtitle>NEW CONTENTS</SectionSubtitle>
                    <SectionTitle>新着</SectionTitle>
                  </SectionTitleWrapper>
                  <SectionUnderline />
                </SectionHeader>

                {/* Contents Grid */}
                {displayedNewContents.length === 0 ? (
                  <NoContents>新着記事はありません</NoContents>
                ) : (
                  <>
                    <ContentsGrid>
                      {displayedNewContents.map((content) => (
                        <ContentCard key={content.id} content={content} />
                      ))}
                    </ContentsGrid>
                    <SectionFooter>
                      <LoadMoreButton
                        href={'/member/contents/search?orderBy=recent&page=1'}
                      >
                        <LoadMoreText>もっと見る</LoadMoreText>
                        <ContentsMoreIcon />
                      </LoadMoreButton>
                    </SectionFooter>
                  </>
                )}
              </Section>

              {/* Tag Search Section */}
              <Section id="tag-search">
                <SectionHeader>
                  <SectionTitleWrapper>
                    <SectionSubtitle>TAG</SectionSubtitle>
                    <SectionTitle>タグ検索</SectionTitle>
                  </SectionTitleWrapper>
                  <SectionUnderline />
                </SectionHeader>

                {/* Tag Grid */}
                <TagsGrid>
                  {tagCategories.map((category) => (
                    <TagCardLink
                      key={category.title}
                      href={searchContents(category.title)}
                    >
                      <TagCard
                        className={`tag-${category.title
                          .replace(/[・\s]/g, '-')
                          .toLowerCase()}`}
                      >
                        <TagOverlay>
                          <TagImage src={category.image} alt={category.title} />
                        </TagOverlay>
                        <TagLabel>
                          <TagText>{category.title}</TagText>
                        </TagLabel>
                      </TagCard>
                    </TagCardLink>
                  ))}
                </TagsGrid>
              </Section>

              {/* Bookmark Section */}
              <Section id="bookmark-contents">
                <SectionHeader>
                  <SectionTitleWrapper>
                    <SectionSubtitle>BOOK MARK</SectionSubtitle>
                    <SectionTitle>お気に入り</SectionTitle>
                  </SectionTitleWrapper>
                  <SectionUnderline />
                </SectionHeader>

                {/* Bookmark Contents Grid */}
                {displayedBookmarkedContents.length === 0 ? (
                  <NoContents>お気に入りの記事はありません</NoContents>
                ) : (
                  <>
                    <ContentsGrid>
                      {displayedBookmarkedContents.map((content) => (
                        <ContentCard key={content.id} content={content} />
                      ))}
                    </ContentsGrid>

                    <SectionFooter>
                      <LoadMoreButton
                        href={
                          '/member/contents/search?page=1&filterBy=favorite'
                        }
                      >
                        <LoadMoreText>もっと見る</LoadMoreText>
                        <ContentsMoreIcon />
                      </LoadMoreButton>
                    </SectionFooter>
                  </>
                )}
              </Section>
            </MainContent>

            {/* Right Column - Rankings */}
            <RankingsContainer>
              {/* Ranking Tabs (Mobile) */}
              {popularContents.length > 0 && (
                <RankingTabs>
                  <RankingTab
                    className={`ranking-popular ${
                      activeRankingTab === 'popular' ? 'active' : ''
                    }`}
                    onClick={() => setActiveRankingTab('popular')}
                  >
                    <RankingTabSubtitle>いいね！</RankingTabSubtitle>
                    <RankingTabTitle>人気記事</RankingTabTitle>
                  </RankingTab>
                  {weeklyContents.length > 0 && (
                    <RankingTab
                      className={`ranking-weekly ${
                        activeRankingTab === 'weekly' ? 'active' : ''
                      }`}
                      onClick={() => setActiveRankingTab('weekly')}
                    >
                      <RankingTabBadge>
                        <CrownIcon />
                      </RankingTabBadge>
                      <RankingTabTitle>週間ランキング</RankingTabTitle>
                    </RankingTab>
                  )}
                </RankingTabs>
              )}
              {/* Popular Articles Ranking */}
              {popularContents.length > 0 && (
                <RankingCard
                  className={`ranking-popular ${
                    activeRankingTab === 'popular' ? 'active' : ''
                  }`}
                  style={{
                    display: activeRankingTab === 'popular' ? 'block' : 'none',
                  }}
                >
                  <RankingHeader>
                    <RankingSubtitle>いいね！</RankingSubtitle>
                    <RankingTitle>人気記事</RankingTitle>
                  </RankingHeader>

                  <RankingContent>
                    {popularContents.slice(0, 5).map((content, index) => (
                      <RankingItemLink
                        key={content.id}
                        href={contentDetail(content.id)}
                      >
                        <RankingItem>
                          <RankingItemInfo>
                            <RankingThumbnail>
                              <RankingImage
                                src={
                                  content.imageUrl ||
                                  '/path/to/article-image.jpg'
                                }
                                alt={content.title}
                              />
                            </RankingThumbnail>
                            <RankingDetails>
                              <RankingItemTitle>
                                {content.title}
                              </RankingItemTitle>
                            </RankingDetails>
                          </RankingItemInfo>
                          <RankingTags>
                            {content.contentTags.slice(0, 3).map((tag) => (
                              <RankingTag key={tag.name}>
                                #{tag.name}
                              </RankingTag>
                            ))}
                          </RankingTags>
                          <RankingStats className="popular">
                            いいね {content.likesCount}
                          </RankingStats>
                          <RankingNumber>{index + 1}</RankingNumber>
                        </RankingItem>
                      </RankingItemLink>
                    ))}
                  </RankingContent>

                  <RankingFooter>
                    <RankingLink
                      href={'/member/contents/search?orderBy=likes&page=1'}
                    >
                      <RankingLinkText>ランキング一覧</RankingLinkText>
                      <ContentsMoreIcon />
                    </RankingLink>
                  </RankingFooter>
                </RankingCard>
              )}

              {/* Weekly Ranking */}
              {weeklyContents.length > 0 && (
                <RankingCard
                  className={`ranking-weekly ${
                    activeRankingTab === 'weekly' ? 'active' : ''
                  }`}
                  style={{
                    display: activeRankingTab === 'weekly' ? 'block' : 'none',
                  }}
                >
                  <RankingHeader>
                    <WeeklyBadge>
                      <CrownIcon />
                    </WeeklyBadge>
                    <RankingTitle>週間ランキング</RankingTitle>
                  </RankingHeader>

                  <RankingContent>
                    {weeklyContents.map((content, index) => (
                      <RankingItemLink
                        key={content.id}
                        href={contentDetail(content.id)}
                      >
                        <RankingItem>
                          <RankingItemInfo>
                            <RankingThumbnail>
                              <RankingImage
                                src={
                                  content.imageUrl ||
                                  '/path/to/article-image.jpg'
                                }
                                alt={content.title}
                              />
                            </RankingThumbnail>
                            <RankingDetails>
                              <RankingItemTitle>
                                {content.title}
                              </RankingItemTitle>
                            </RankingDetails>
                          </RankingItemInfo>
                          <RankingTags>
                            {content.contentTags.map((tag) => (
                              <RankingTag key={tag.name}>
                                #{tag.name}
                              </RankingTag>
                            ))}
                          </RankingTags>
                          {/* TODO: ユーザー数が増えたらPV数表示を復活させる */}
                          {/* <RankingStats className="weekly">
                            PV {content.viewCount}
                          </RankingStats> */}
                          <RankingNumber>{index + 1}</RankingNumber>
                        </RankingItem>
                      </RankingItemLink>
                    ))}
                  </RankingContent>
                </RankingCard>
              )}
            </RankingsContainer>
          </ContentArea>
        </MainLayout>
      </Layout>
    </>
  )
}

// Styled Components
const Layout = styled.div`
  min-height: 100vh;
  background-color: white;
  padding: 32px 16px;
  max-width: 1236px;
  margin: auto;
`

const SearchHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin: 0 auto 32px;
  gap: 8px;

  @media screen and (max-width: 767px) {
    margin-bottom: 40px;
  }
`

const SearchContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
`

const SearchTitle = styled.h1`
  font-size: 28px;
  font-weight: ${fontWeightBold};
  line-height: 1.5;
  background: rgba(255, 255, 255, 0.9);
`

const SearchWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;

  @media screen and (max-width: 767px) {
    width: 100%;
  }
`

const SearchInput = styled.input`
  width: 413px;
  padding: 14px 20px 14px 20px;
  padding-right: 48px;
  font-size: 16px;
  font-weight: 700;
  color: #393e4c;
  border: 1px solid #808080;
  border-radius: 6px;
  background-color: white;
  font-family: 'Noto Sans JP', sans-serif;

  &::placeholder {
    color: #cdcdcd;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px #f29600;
  }

  @media screen and (max-width: 767px) {
    width: 100%;
  }

  @media (min-width: 992px) {
    width: 384px;
  }
`

const SearchIconWrapper = styled.div`
  position: absolute;
  right: 12px;
  width: 24px;
  height: 24px;
  color: #cdcdcd;
  cursor: pointer;
`

const MainLayout = styled.div`
  display: flex;
  justify-content: center;
  gap: 40px;

  @media (max-width: 992px) {
    gap: 32px;
  }

  @media (max-width: 768px) {
    display: block;
  }
`

const Sidebar = styled.aside`
  min-width: 154px;
  border-left: 1px solid #e5e7eb;

  @media (max-width: 991px) {
    display: none;
  }
`

const SidebarNav = styled.nav`
  position: sticky;
  top: 90px;
  height: fit-content;
  display: flex;
  flex-direction: column;
  margin-left: -1px;
  gap: 24px;
`

const NavItem = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 16px;
  gap: 10px;
  cursor: pointer;

  &.active {
    border-left: 4px solid #f29600;
  }
`

const NavText = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #393e4c;

  ${NavItem}:not(.active) & {
    color: #6b7280;
  }
`

const ContentArea = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 40px;

  @media (max-width: 1279px) {
    flex-direction: column;
  }
`

const MainContent = styled.div`
  width: 720px;
  display: flex;
  flex-direction: column;
  gap: 80px;

  @media (max-width: 1279px) {
    width: 100%;
    gap: 64px;
  }
`

const Section = styled.section``

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  margin-bottom: 40px;
  @media screen and (max-width: 768px) {
    margin-bottom: 32px;
  }
`

const SectionTitleWrapper = styled.div`
  position: relative;
`

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: black;
  font-family: 'Noto Sans JP', sans-serif;
`

const SectionSubtitle = styled.h1`
  font-size: 26px;
  line-height: 1;
  font-weight: 300;
  color: #666;
  font-family: 'Lato', sans-serif;
`

const SectionUnderline = styled.div`
  width: 100%;
  height: 2px;
  background-color: #f29600;
`

const ContentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, calc(50% - 20px));
  gap: 40px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 100%;
    gap: 32px;
  }
`

const NoContents = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #393e4c;
  font-family: 'Noto Sans JP', sans-serif;
  padding: 100px 20px;
  border-top-right-radius: 64px;
  border-bottom-left-radius: 64px;
  text-align: center;
  background: #f8f8f8;
`

const SectionFooter = styled.div`
  position: relative;
  padding: 8px;
  margin-top: -1px;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    padding: 0;
  }
`

const LoadMoreButton = styled.a`
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 24px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 16px;
  font-family: 'Noto Sans JP', sans-serif;
  background-color: white;
  border: 1px solid #f29600;
  color: #f29600;
  min-height: 47px;
  width: 259px;
  justify-content: center;
  cursor: pointer;

  svg {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
  }

  &:hover {
    background: #f29600;
    color: white;
    svg {
      path {
        stroke: white;
      }
    }
  }
`

const LoadMoreText = styled.span`
  flex: 1;
  text-align: center;
`

const TagsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  width: 100%;

  @media (min-width: 992px) {
    grid-template-columns: 1fr 1fr;
  }
`

const TagCard = styled.div`
  position: relative;
  border-radius: 6px;
  overflow: hidden;
`

const TagOverlay = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

const TagLabel = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  display: inline-flex;
  padding: 4px 16px;
  justify-content: center;
  align-items: center;
  background-color: white;
`

const TagText = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #393e4c;
  font-family: 'Noto Sans JP', sans-serif;
`

const TagCardLink = styled.a`
  text-decoration: none;
  color: inherit;
`

const TagImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const RankingTabs = styled.div`
  display: none;
  justify-content: center;
  align-items: center;
  gap: 24px;
  width: 100%;

  @media (max-width: 767px) {
    display: flex;
    position: relative;
  }
`

const RankingTab = styled.button`
  position: relative;
  z-index: 1;
  width: calc(50% - 18px);
  height: 55px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin: 0;
  border-width: 2px;
  border-bottom: none;
  border-top-right-radius: 5px;
  border-top-left-radius: 5px;
  padding-top: 0;
  background: none;
  cursor: pointer;

  &.ranking-popular {
    background: #fef3e1;
    border-color: #ff6c01;
  }

  &.ranking-weekly {
    background: #e6f4f2;
    border-color: #02b7b0;
  }
`

const RankingTabSubtitle = styled.div`
  position: relative;
  font-size: 12px;
  color: #f29600;

  &:before {
    content: '';
    position: absolute;
    left: -11px;
    top: 9px;
    line-height: 1;
    width: 12px;
    border-top: 2px solid #f29600;
    transform: rotate(50deg);
  }

  &:after {
    content: '';
    position: absolute;
    right: -8px;
    top: 9px;
    line-height: 1;
    width: 12px;
    border-top: 2px solid #f29600;
    transform: rotate(-50deg);
  }
`

const RankingTabTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
`

const RankingTabBadge = styled.div`
  width: 32px;
`

const RankingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 250px;
  position: sticky;
  top: 90px;

  @media (max-width: 1279px) {
    position: static;
    align-items: flex-start;
    flex-direction: row;
    max-width: 100%;
    gap: 40px;
  }

  @media (max-width: 767px) {
    display: block;
  }
`

const RankingCard = styled.div`
  position: relative;

  @media (max-width: 767px) {
    margin-top: -2px;
  }

  &.ranking-popular {
    background: #fef3e1;
    @media (max-width: 767px) {
      border-top: 2px solid #ff6c01;
    }
  }

  &.ranking-weekly {
    background: #e6f4f2;
    border-top: 2px solid #02b7b0;
  }

  @media (max-width: 1279px) {
    width: calc(50% - 20px);
  }

  @media (min-width: 768px) {
    display: block !important;
  }

  @media (max-width: 767px) {
    width: 100%;
    display: none;

    &.active {
      display: block;
    }
  }
`

const RankingHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 4px;
  padding-top: 17px;

  @media (max-width: 767px) {
    display: none;
  }
`

const RankingSubtitle = styled.div`
  position: relative;
  font-size: 14px;
  font-weight: 700;
  color: #f29600;
  text-align: center;
  font-family: 'Noto Sans JP', sans-serif;
  padding-left: 4px;
  padding-right: 4px;
  line-height: 1;

  &:before {
    content: '';
    position: absolute;
    left: -9px;
    top: 7px;
    line-height: 1;
    width: 12px;
    border-top: 2px solid #f29600;
    transform: rotate(50deg);
  }

  &:after {
    content: '';
    position: absolute;
    right: -6px;
    top: 7px;
    line-height: 1;
    width: 12px;
    border-top: 2px solid #f29600;
    transform: rotate(-50deg);
  }
`

const RankingTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: black;
  text-align: center;
  font-family: 'Noto Sans JP', sans-serif;
`

const WeeklyBadge = styled.div`
  width: 32px;
`

const RankingContent = styled.div``

const RankingItem = styled.div`
  position: relative;
  padding: 20px;
  border-bottom: 1px solid white;

  @media screen and (max-width: 767px) {
    padding: 28px 16px 12px;
  }
`

const RankingItemInfo = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  padding-right: 8px;
  gap: 10px;
`

const RankingThumbnail = styled.div`
  width: 52px;
  height: 34px;
  background-color: #d1d5db;
  flex-shrink: 0;

  @media screen and (max-width: 767px) {
    width: 86px;
    height: 56px;
  }
`

const RankingImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const RankingDetails = styled.div`
  flex: 1;
`

const RankingItemTitle = styled.h4`
  font-size: 14px;
  font-weight: 700;
  color: black;
  line-height: 140%;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-family: 'Noto Sans JP', sans-serif;
  word-break: break-all;
`

const RankingTags = styled.div`
  display: flex;
  gap: 11px;
  margin-top: 6px;
  flex-wrap: wrap;

  @media screen and (max-width: 767px) {
    margin-top: 2px;
  }
`

const RankingTag = styled.span`
  font-size: 10px;
  font-weight: 400;
  color: #6b7280;
  font-family: 'Noto Sans JP', sans-serif;

  @media screen and (max-width: 767px) {
    font-size: 11px;
  }
`

const RankingStats = styled.div`
  font-size: 10px;
  font-weight: 400;
  color: #f29600;
  margin-top: 2px;
  text-align: right;
  font-family: 'Noto Sans JP', sans-serif;

  @media screen and (max-width: 767px) {
    font-size: 10px;
  }

  &.weekly {
    color: #02b7b0;
  }
`

const RankingNumber = styled.div`
  position: absolute;
  right: 12px;
  top: 3.5px;
  font-size: 55px;
  font-weight: 700;
  font-style: italic;
  color: white;
  line-height: 1.24;
  letter-spacing: -1px;
  font-family: 'Lato', sans-serif;
  @media screen and (max-width: 767px) {
    top: 15px;
  }
`

const RankingFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 25px;
`

const RankingLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  svg {
    width: 20px;
    path {
      stroke: black;
    }
  }
`

const RankingLinkText = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: black;
  font-family: 'Noto Sans JP', sans-serif;
`

const RankingItemLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;

  &:hover {
    opacity: 0.8;
  }
`
